// Transforms the cumulative (party-perspective) API responses into the
// `portfolio` and `requisitions` shapes the reconciliation UI consumes.
// Keeps all API→UI mapping in one place so the components stay unchanged.

// Derive a reconciliation status from a supply/return daily row.
// delta_mwh = actual - scheduled (negative => under-drawn, positive => over-drawn).
const deriveStatus = (row) => {
  const scheduled = row?.scheduled_mwh ?? 0;
  const actual = row?.actual_mwh ?? 0;
  const delta = row?.delta_mwh ?? actual - scheduled;

  if (actual === 0 && scheduled > 0) return "DRAWAL_PENDING";
  if (delta > 0) return "OVER_DRAWN";
  if (delta < 0) return "BALANCE_LEFT";
  return "RECONCILED";
};

// A daily entry has no per-row banking factor; approximate from the
// portfolio-level average when available.
const rowBf = (overview) =>
  overview?.avg_banking_factor_pct != null
    ? Math.round(overview.avg_banking_factor_pct)
    : null;

// Build one requisition row from a supply/return daily entry.
const toRequisition = (row, index, overview) => {
  const scheduled = row?.scheduled_mwh ?? 0;
  const actual = row?.actual_mwh ?? 0;
  const delta = row?.delta_mwh ?? actual - scheduled;
  const status = deriveStatus(row);
  const approval = row?.approval_nos?.[0];
  const category = row?.categories?.[0];

  return {
    id: approval || `${row?.date || "ROW"}-${index}`,
    name: approval || category || row?.date || `Requisition ${index + 1}`,
    injDates: row?.date || "",
    drawDates: category || "",
    bf: rowBf(overview),
    injected: scheduled,
    // Drawable target for the day is the scheduled quantum.
    drawable: scheduled,
    // Nothing actually drawn yet => show as pending (null renders as "—").
    drawn: status === "DRAWAL_PENDING" ? null : actual,
    // Remaining still to be drawn (positive) or excess (negative).
    remaining: -delta,
    status,
  };
};

export const mapRequisitions = (supply, overview) => {
  const daily = supply?.daily;
  if (!Array.isArray(daily)) return [];
  return daily.map((row, i) => toRequisition(row, i, overview));
};

// ---------------------------------------------------------------------------
// Requisition rows from /banking/connection/list (data.page[]).
// Each connection = one row. Values come from the counterparty listing's
// finalizedConnectionValues (bankingFactor, slotInjects, slotDrawls).
// ---------------------------------------------------------------------------

const sumQty = (slots) =>
  Array.isArray(slots)
    ? slots.reduce((acc, s) => acc + (s?.requiredQty || 0), 0)
    : 0;

// "dd-MMM-yyyy" -> sortable timestamp (slots aren't guaranteed date-ordered).
const parseSlotDate = (str) => {
  const t = str ? Date.parse(str.replace(/-/g, " ")) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

// Compact "01-Aug-2026 – 30-Sep-2026" range spanning all slots (min start, max end).
const slotDateRange = (slots) => {
  if (!Array.isArray(slots) || slots.length === 0) return "";
  const starts = slots
    .map((s) => s?.dateStartAt)
    .filter(Boolean)
    .sort((a, b) => parseSlotDate(a) - parseSlotDate(b));
  const ends = slots
    .map((s) => s?.dateEndAt)
    .filter(Boolean)
    .sort((a, b) => parseSlotDate(a) - parseSlotDate(b));
  const first = starts[0];
  const last = ends[ends.length - 1];
  if (!first) return "";
  return last && last !== first ? `${first} – ${last}` : first;
};

const connectionToRequisition = (connection, index) => {
  // The counterparty listing carries the finalized/agreed values.
  const counterparty = connection?.counterPartyListings?.[0] || {};
  const finalized =
    counterparty.finalizedConnectionValues ||
    connection?.finalizedConnectionValues ||
    {};

  const name =
    counterparty.name ||
    connection?.selfListing?.name ||
    connection?.connectionUniqueId ||
    `Requisition ${index + 1}`;

  const bf =
    finalized.bankingFactor ??
    counterparty.bankingFactor ??
    connection?.selfListing?.bankingFactor ??
    null;

  const slotInjects =
    finalized.slotInjects || counterparty.bankingSlotInjects || [];
  const slotDrawls =
    finalized.slotDrawls || counterparty.bankingSlotDrawls || [];

  const injected = sumQty(slotInjects);
  const drawn = sumQty(slotDrawls);
  // Drawable = injected adjusted by banking factor (BF is a percentage).
  const drawable = bf != null ? (injected * bf) / 100 : injected;
  const remaining = drawable - drawn;

  let status;
  if (drawn === 0 && drawable > 0) status = "DRAWAL_PENDING";
  else if (remaining < 0) status = "OVER_DRAWN";
  else if (remaining > 0) status = "BALANCE_LEFT";
  else status = "RECONCILED";

  const injDates = slotDateRange(slotInjects);
  const drawDates = slotDateRange(slotDrawls);

  return {
    id:
      counterparty.connectionId ||
      connection?.id ||
      connection?.connectionUniqueId ||
      index,
    name,
    contractId:
      counterparty.connectionUniqueId ||
      connection?.connectionUniqueId ||
      null,
    trader:
      counterparty.trader ||
      connection?.trader ||
      connection?.selfListing?.trader ||
      null,
    injDates: injDates ? `Inj ${injDates}` : "",
    drawDates: drawDates ? `Draw ${drawDates}` : "",
    bf: bf != null ? Math.round(bf) : null,
    injected,
    drawable,
    drawn: status === "DRAWAL_PENDING" ? null : drawn,
    remaining,
    status,
  };
};

export const mapConnectionRequisitions = (connections) => {
  if (!Array.isArray(connections)) return [];
  return connections.map((c, i) => connectionToRequisition(c, i));
};

const countStatuses = (requisitions) =>
  requisitions.reduce(
    (acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    },
    { RECONCILED: 0, BALANCE_LEFT: 0, DRAWAL_PENDING: 0, OVER_DRAWN: 0 },
  );

const dateRangeFromDaily = (daily) => {
  if (!Array.isArray(daily) || daily.length === 0) return "";
  const first = daily[0]?.date;
  const last = daily[daily.length - 1]?.date;
  return first && last && first !== last ? `${first} – ${last}` : first || "";
};

export const mapPortfolio = (overview, supply, requisitions) => {
  const rows = requisitions || [];
  const statusCounts = countStatuses(rows);

  const scheduled =
    overview?.total_scheduled_mwh ?? supply?.metrics?.total_scheduled_mwh ?? 0;
  const totalDrawn =
    overview?.total_actual_mwh ?? supply?.metrics?.total_actual_mwh ?? 0;
  const injected = scheduled;
  const drawableQuantum = scheduled;

  const netBalance = scheduled - totalDrawn;
  const drawnPct =
    scheduled > 0 ? Math.round((totalDrawn / scheduled) * 100) : 0;

  // Prefer the actual row count so the tabs/donut totals match the table;
  // fall back to the cumulative contract count when no rows are loaded.
  const totalRequisitions = rows.length || overview?.contracts_combined || 0;
  const needAttention =
    statusCounts.OVER_DRAWN +
    statusCounts.BALANCE_LEFT +
    statusCounts.DRAWAL_PENDING;

  return {
    injected,
    scheduled,
    actual: totalDrawn,
    drawableQuantum,
    totalDrawn,
    netBalance,
    drawnPct,
    totalRequisitions,
    needAttention,
    statusCounts,
    dateRange: dateRangeFromDaily(supply?.daily),
    subtitle: "Compare scheduled energy with actual energy drawn.",
  };
};
